import { defineStore } from 'pinia';
import {useOrdersStore} from "@/console-new/stores/orders.store";
import {useUserStore} from "@/console-new/stores/user.store";
import API from "@/shared/helpers/API";
import EscPosOrders from "@/console-sun/helpers/EscPosOrders";
import EscPosReceipts from "@/console-sun/helpers/EscPosReceipts";
import {useEpsonStore} from "@/console-new/stores/epson.store";
import {useCounterStore} from "@/console-new/stores/counter.store";
import {useLanguageStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import {useAblyStore} from "@/console-new/stores/ably.store";
import {isNative} from "@/shared/helpers/webapp";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";
import {useFunnelStore} from "@/console-new/stores/funnel.store";
import {formatDateOnly} from "@/shared/helpers/dates";

export const useOrdersActionsStore = defineStore('ordersActions', {
	state: () => ({
		ordersPayment: null,
	}),
	getters: {
		sunmiStore() {
			return useSunmiStore();
		},
	},
	actions: {
		sendSms(ordersIds, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			API.init().sendSms(userStore.currBusinessId, JSON.stringify(ordersIds), function (response) {
				if (response && response.result === 'OK') {
					callback && callback(true);
				} else {
					callback && callback(false, response?.details || null);
				}
			});
		},
		sendOrderMessage(ordersIds, message, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			API.init().addOrderMessage(userStore.currBusinessId, JSON.stringify(ordersIds), message, function (response) {
				if (response && response.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
				}
			});
		},
		ordersGroupAction(orderGroup) {

			let languageStore = useLanguageStore();
			let userStore = useUserStore();
			let ordersStore = useOrdersStore();

			if(!orderGroup) {
				return {
					text: languageStore.getString("unknown"),
					actionId: "null",
					iconName: "icon-order-complete"
				};
			}

			if(orderGroup.status === ordersStore.STATUS.completed || orderGroup.status === ordersStore.STATUS.canceled || orderGroup.status === ordersStore.STATUS.failed) {
				return {
					text: languageStore.getString("completed"),
					actionId: "null",
					iconName: "icon-order-complete"
				};
			}

			let grandTotal = orderGroup.orders.reduce((acc, order) => { return acc + order.grandTotal; }, 0);
			let advancedPayments = !orderGroup.paid && userStore.currPlans.counter && grandTotal > 0;

			if(orderGroup.status === ordersStore.STATUS.confirmed) {
				let refutable = ["delivery", "takeaway"].includes(orderGroup.order_mode);
				if(refutable) {
					return {
						text: languageStore.getString("accept"),
						actionId: "accept",
						iconName: "icon-order-accept"
					};
				} else {
					return {
						text: languageStore.getString("prepare"),
						actionId: "prepare",
						iconName: "icon-order-prepare"
					};
				}
			} else if(orderGroup.status === ordersStore.STATUS.accepted) {
				return {
					text: languageStore.getString("prepare"),
					actionId: "prepare",
					iconName: "icon-order-prepare"
				};
			} else if(orderGroup.status === ordersStore.STATUS.preparing) {
				if(userStore.currBusiness.counter_courses) {
					let lastCourse = orderGroup.orders.every(order => order.course >= order.maxCourse);
					if(lastCourse) {
						return {
							text: advancedPayments ? languageStore.getString("payment") : languageStore.getString("completed"),
							actionId: advancedPayments ? "payment" : "complete",
							iconName: advancedPayments ? "icon-payment-white" : "icon-order-complete"
						};
					} else {
						return {
							text: languageStore.getString("next_course"),
							actionId: "next",
							iconName: "icon-order-prepare"
						};
					}
				} else {
					return {
						text: advancedPayments ? languageStore.getString("payment") : languageStore.getString("completed"),
						actionId: advancedPayments ? "payment" : "complete",
						iconName: advancedPayments ? "icon-payment-white" : "icon-order-complete"
					};
				}
			} else if(orderGroup.status === ordersStore.STATUS.prepared) {
				return {
					text: orderGroup.paid ? languageStore.getString("completed") : languageStore.getString("payment"),
					actionId: advancedPayments ? "payment" : "complete",
					iconName: advancedPayments ? "icon-payment-white" : "icon-order-complete"
				};
			} else {
				return {
					text: languageStore.getString("unknown"),
					actionId: "null",
					iconName: "icon-order-complete"
				};
			}

		},
		performAction(actionId, orderGroup, callback) {

			let additionalData = null;
			let newStatus = null;
			let removeSearchIfNeeded = false;
			if(actionId === "accept") {
				newStatus = "accepted";
			} else if(actionId === "prepare") {
				newStatus = "preparing";
			} else if(actionId === "prepare_done") {
				newStatus = "prepared";
			} else if(actionId === "complete") {
				newStatus = "completed";
				removeSearchIfNeeded = true;
			} else if(actionId === "cancel" || actionId === "refuse") {
				newStatus = "canceled";
				removeSearchIfNeeded = true;
			} else if(actionId === "payment") {
				this.ordersPayment = orderGroup.orders;
				callback && callback(true, null);
				return;
			} else if(actionId === "next") {
				newStatus = "preparing";
			}

			let ordersIds = [];
			orderGroup.orders.forEach(order => {
				ordersIds.push(order.order_id);
			});

			this.changeOrders(ordersIds, actionId, (success, details) => {
				if(success) {

					const ordersStore = useOrdersStore();

					if(removeSearchIfNeeded) {

						let search = ordersStore.search;
						if(search && (search.startsWith("id:") || search.startsWith("tid:"))) {
							ordersStore.showCompleted = false;
							ordersStore.search = null;
							ordersStore.page = 0;
							ordersStore.refreshOrders();
							callback && callback(success, details);
							return;
						}

					}

					let updatesArray = [];
					orderGroup.orders.forEach(order => {
						let update = {
							order_id: order.order_id,
							status: newStatus,
						}
						if(actionId === "next" && details) {
							update.course = Number(details);
						}
						updatesArray.push(update);
					});
					ordersStore.applyOrdersUpdates(updatesArray, false);

				}
				callback && callback(success, details);
			});
		},
		performActionSingleOrder(actionId, order, callback) {

			let newStatus = null;
			if(actionId === "accept") {
				newStatus = "accepted";
			} else if(actionId === "prepare") {
				newStatus = "preparing";
			} else if(actionId === "prepare_done") {
				newStatus = "prepared";
			} else if(actionId === "complete") {
				newStatus = "completed";
			} else if(actionId === "cancel") {
				newStatus = "canceled";
			} else if(actionId === "payment") {
				this.ordersPayment = [order];
				callback && callback(true);
				return;
			} else if(actionId === "next") {
				newStatus = "preparing";
			}

			this.changeOrders([order.order_id], actionId, (success, details) => {
				if(success) {

					let updatesArray = [];
					let update = {
						order_id: order.order_id,
						status: newStatus,
					};
					if(actionId === "next" && details) {
						update.course = Number(details);
					}
					updatesArray.push(update);
					const ordersStore = useOrdersStore();
					ordersStore.applyOrdersUpdates(updatesArray, false);

				}
				callback && callback(success);
			});
		},
		changeOrders(ordersIds, action, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			API.init().changeOrders(userStore.currBusinessId, JSON.stringify(ordersIds), action, false, resp => {
				if (resp && resp.result === "OK") {
					callback && callback(true, resp.new_course ? resp.new_course : null);
				} else {
					callback && callback(false, resp.details ? resp.details : null);
				}
			});
		},
		uniteOrders(ordersIds, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}
			API.init().uniteOrders(userStore.currBusinessId, JSON.stringify(ordersIds), resp => {
				if (resp && resp.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false, resp.details ? resp.details : null);
				}
			});
		},
		printAggregateOrder(orderGroup, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId || !orderGroup?.orders) {
				return;
			}

			let orderIds = [];
			if(orderGroup) {
				orderGroup.orders.forEach(order => {
					orderIds.push(order.order_id);
				});
			}

			let didLocalPrint = false;
			if(this.sunmiStore.canLocalPrint) {
				let resultToPrint = EscPosOrders.init().getOrdersRawData(orderGroup.orders);
				if(resultToPrint !== null && resultToPrint !== "") {
					this.sunmiStore.enqueuePrint(resultToPrint);
					didLocalPrint = true;
				}
			}

			API.init().printAggregateReceipt(userStore.currBusinessId, JSON.stringify(orderIds), (response) => {
				if (response && response.result === "OK") {
					callback && callback(true);
				} else if (response && response.result === "KO" && response.details === "no_printers_found") {
					if(!didLocalPrint) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification('no-printers-found');
					}
					callback && callback(didLocalPrint);
				} else {
					if(!didLocalPrint) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification("generic-fail");
					}
					callback && callback(false);
				}
			});
		},
		printPrebill(orderGroup, allItems, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId || !orderGroup?.orders) {
				return;
			}

			let orderIds = [];
			if(orderGroup) {
				orderGroup.orders.forEach(order => {
					orderIds.push(order.order_id);
				});
			}

			let didLocalPrint = false;
			if(this.sunmiStore.canLocalPrint) {
				let prebillBusiness = {
					name: userStore.currBusiness?.name || "",
					address: userStore.currBusiness?.address?.via || "",
					civic: userStore.currBusiness?.address?.civico || null,
					zip: userStore.currBusiness?.address?.zip || "",
					city: userStore.currBusiness?.address?.comune || "",
					province: userStore.currBusiness?.address?.provincia_codice || "",
					vat: userStore.currBusiness?.vat || "",
				};

				let products = [];
				for(let order of orderGroup.orders) {
					for(let item of order.items) {
						let quantity = Number(item.quantity);
						if(!allItems && item.redeemed > 0) {
							quantity -= Number(item.redeemed);
						}
						if(quantity <= 0) continue;
						let price = Number(item.price);
						for(let product of (item.products || [])) {
							price += Number(product.price) * Number(product.quantity);
						}
						if(price <= 0) continue;
						let itemVat = item.vat === null ? 10 : Number(item?.vat || 0);
						itemVat = isNaN(itemVat) ? 10 : itemVat;
						products.push({ name: item.name, quantity, price, vat: itemVat, discount: 0 });
					}
				}

				// Calculate total discount/surcharge and cover charges from orders (matching backend ActionsController:getReceiptFromOrders)
				let paymentDiscount = 0;
				let totalAdditionalCosts = 0;
				for(let order of orderGroup.orders) {
					let orderAdditionalCosts = (order.subtotal_delta || 0) + (order.delivery_costs_value || 0) + (order.takeaway_costs_value || 0);
					if(orderAdditionalCosts < 0) {
						paymentDiscount += Math.abs(orderAdditionalCosts);
					} else if(orderAdditionalCosts > 0) {
						totalAdditionalCosts += orderAdditionalCosts;
					}
					let coverNum = Number(order.cover_charge_num || 0);
					let coverValue = Number(order.cover_charge_value || 0);
					if(coverNum > 0) {
						let coverName = order.room?.cover_charge_name || "Coperto";
						products.push({ name: coverName, quantity: coverNum, price: coverValue, vat: 10, discount: 0 });
					}
				}
				if(totalAdditionalCosts > 0) {
					products.push({ name: "Maggiorazione", quantity: 1, price: totalAdditionalCosts, vat: 10, discount: 0 });
				}

				if(paymentDiscount > 0 && products.length > 0) {
					let totalAmount = 0;
					for(let product of products) {
						totalAmount += product.price * product.quantity;
					}
					if(totalAmount > 0) {
						let distributedDiscount = 0;
						for(let product of products) {
							let productSubtotal = product.price * product.quantity;
							let proportionalDiscount = Math.floor((productSubtotal / totalAmount) * paymentDiscount);
							product.discount = proportionalDiscount;
							distributedDiscount += proportionalDiscount;
						}
						// Handle rounding remainder
						let remainder = paymentDiscount - distributedDiscount;
						let productIndex = 0;
						while(remainder > 0 && productIndex < products.length) {
							let productSubtotal = products[productIndex].price * products[productIndex].quantity;
							let currentDiscount = products[productIndex].discount;
							if(currentDiscount < productSubtotal) {
								let availableSpace = productSubtotal - currentDiscount;
								let toAllocate = Math.min(remainder, availableSpace);
								products[productIndex].discount += toAllocate;
								remainder -= toAllocate;
							}
							productIndex++;
						}
					}
				}

				let additionalLines = [];
				let tableName = null;
				for(let order of orderGroup.orders) {
					if(!tableName && order.table_number) tableName = order.table_number;
					let orderNum = Number(order.order_number_day || 0);
					if(orderNum < 10) orderNum = "00" + orderNum;
					else if(orderNum < 100) orderNum = "0" + orderNum;
					additionalLines.push("Ordine #" + orderNum);
				}
				if(tableName) additionalLines.push("-> " + tableName);
				additionalLines.push("Powered by qromo.it");

				let rawData = EscPosReceipts.init().getPrintReceipt(prebillBusiness, {products}, true, additionalLines);
				this.sunmiStore.enqueuePrint(rawData);
				didLocalPrint = true;
			}

			API.init().printPrebill(userStore.currBusinessId, JSON.stringify(orderIds), allItems, (response) => {
				if (response && response.result === "OK") {
					callback && callback(true);
				} else if (response && response.result === "KO" && response.details === "no_printers_found") {
					if(!didLocalPrint) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification('no-printers-found');
					}
					callback && callback(didLocalPrint);
				} else {
					if(!didLocalPrint) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification("generic-fail");
					}
					callback && callback(false);
				}
			});
		},
		printPosReceipts(id, callback) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				return;
			}

			API.init().printPosReceipts(userStore.currBusinessId, id, (response) => {
				if (response && response.result === "OK") {
					callback && callback(true);
				} else if (response && response.result === "KO" && response.details === "no_printers_found") {
					if(!this.sunmiStore.canLocalPrint) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification('no-printers-found');
					}
					callback && callback(!!this.sunmiStore.canLocalPrint);
				} else {
					if(!isNative()) {
						let notificationStore = useNotificationStore();
						notificationStore.showNotification("generic-fail");
					}
					callback && callback(false);
				}
			});
		},
		setExpectingFiscalPrint() {
			if(this.sunmiStore.canLocalPrint && useUserStore().currBusiness?.apiscontrino_id) this.sunmiStore.expectingFiscalPrint = true;
		},
		paymentGroupDone(paymentType, amountPartial, itemsSplit, dutchPeople, orderAssociatedId, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, callback) {
			if(!this.ordersPayment) {
				callback && callback(false);
				return;
			}
			const ordersStore = useOrdersStore();
			let orderIds = this.ordersPayment.map(o => o.order_id);

			let userStore = useUserStore();
			if(!userStore.currBusinessId) {
				callback && callback(false);
				return;
			}

			const counterStore = useCounterStore();
			let invoiceData = null;
			if (counterStore.selectedCustomer !== null) {
				let invoiceDataObj = {
					customer_id: counterStore.selectedCustomer.customer_id,
					customer_type: counterStore.selectedCustomer.type,
				};
				if(!!paymentType && ["wire", "check"].includes(paymentType)) {
					invoiceDataObj.invoice_paid = counterStore.setInvoiceAsPaid;
				}
				if(counterStore.invoiceAdditionalData?.purchase_order) {
					invoiceDataObj.purchase_order = counterStore.invoiceAdditionalData.purchase_order;
				}
				if(!invoiceDataObj.invoice_paid && counterStore.invoiceAdditionalData?.payment_due_date) {
					invoiceDataObj.payment_due_date = formatDateOnly(counterStore.invoiceAdditionalData.payment_due_date);
				}
				invoiceData = JSON.stringify(invoiceDataObj);
			}

			if(orderAssociatedId) {

				//Webhook does the job
				ordersStore.refreshOrders((success) => {
					callback && callback(success);
				});

			} else {

				let currentAction = this.ordersGroupAction(ordersStore.selectedOrderGroup);
				this.setExpectingFiscalPrint();

				if(!amountPartial) {

					API.init().changeOrdersPayment(userStore.currBusinessId, JSON.stringify(orderIds), paymentType, discountOnTotal, appliedCoupons, invoiceData, printSingleItem, resp => {
						if (resp && resp.result === "OK") {

							if(userStore.currSubscription === 'free' && resp.weekly_cash_transactions) {
								userStore.currBusiness.weekly_cash_transactions = Number(resp.weekly_cash_transactions || 0);
							}

							if (invoiceData && invoiceData.customer_type === "company" && paymentType !== "paylater" && paymentType !== "manual") {
								const notificationStore = useNotificationStore();
								if (!resp.invoice || resp.invoice.error !== null) {
									notificationStore.showNotification('invoice-request-failed');
								} else {
									notificationStore.showNotification('invoice-request-success');
								}
							}

							let updatesArray = [];
							orderIds.forEach(orderId => {

								let update = {
									order_id: orderId,
									paid: 1,
									status: currentAction && currentAction.actionId === 'next' && userStore.currBusiness.counter_courses ? 'preparing' : "completed",
									type: paymentType,
								};
								if(discountOnTotal) {
									update.subtotal_delta = discountOnTotal;
								}
								updatesArray.push(update);

								let orderPayment = this.ordersPayment ? this.ordersPayment.find(o => o.order_id === orderId) : null;
								if(orderPayment) {
									orderPayment.type = paymentType;
									orderPayment.paid = true;
								}
							});

							ordersStore.applyOrdersUpdates(updatesArray, false, null, (refreshed) => {

								let search = ordersStore.search;
								if(search && (search.startsWith("id:") || search.startsWith("tid:"))) {
									ordersStore.showCompleted = false;
									ordersStore.search = null;
									ordersStore.page = 0;
									ordersStore.refreshOrders();
								}

								callback && callback(true);

							});


						} else {
							if(resp?.details === "cash_limit_exceeded") {
								let maxFreeCashAmount = useCurrencyStore().currCurrency?.max_free_weekly_cash_amount || 300000;
								let weeklyCashTransactions = Number(resp?.weekly_cash_transactions || 0);
								let formattedMaxCash = useCurrencyStore().readableCurrencyValue(maxFreeCashAmount, true, true, true);
								useUserStore().currBusiness.weekly_cash_transactions = weeklyCashTransactions || maxFreeCashAmount;
								useNotificationStore().showNotification("cash-limit-exceeded", [formattedMaxCash]);
							} else if(resp?.details === "customer_not_found") {
								useNotificationStore().showNotification("warning-customer-missing");
							}
							callback && callback(false);
						}
					});

				} else {

					this.addOrderMultiPayment(orderIds, paymentType, amountPartial, invoiceData, itemsSplit, dutchPeople, discountOnTotal, discountPartial, appliedCoupons, resp => {
						if(resp === "success") {

							const ordersStore = useOrdersStore();
							ordersStore.refreshOrders((success) => {
								callback && callback(success);
							});

						} else {
							let notificationStore = useNotificationStore();
							if(resp === "invalid_enums") {
								notificationStore.showNotification('invalid-payment-method');
							} else if(resp === "cash_limit_exceeded") {
								let maxFreeCashAmount = useCurrencyStore().currCurrency?.max_free_weekly_cash_amount || 300000;
								let formattedMaxCash = useCurrencyStore().readableCurrencyValue(maxFreeCashAmount, true, true, true);
								notificationStore.showNotification("cash-limit-exceeded", [formattedMaxCash]);
							}
							callback && callback(false);
						}
					});

				}

			}

		},
		addOrderMultiPayment(orderIds, type, amountPaidPartial, invoiceData, itemsSplit, dutchPeople, discountOnTotal, discountPartial, appliedCoupons, callback = null) {

			let userStore = useUserStore();
			API.init().addOrderMultiPayment(userStore.currBusinessId, JSON.stringify(orderIds), type, amountPaidPartial, invoiceData, JSON.stringify(itemsSplit), JSON.stringify(dutchPeople), discountOnTotal, discountPartial, appliedCoupons, r => {
				if(r && r.result) {
					if(r.result === "OK") {
						if(userStore.currSubscription === 'free' && r.weekly_cash_transactions) {
							userStore.currBusiness.weekly_cash_transactions = Number(r.weekly_cash_transactions || 0);
						}
						callback && callback("success");
					} else if(r.details) {
						if(r.details === "cash_limit_exceeded") {
							useUserStore().currBusiness.weekly_cash_transactions = Number(r?.weekly_cash_transactions || 0);
						}
						callback && callback(r.details);
					} else {
						callback && callback("error");
					}
				} else {
					callback && callback("error");
				}
			});

		},
	}
});
